"use client"

import React, { useState, useRef } from "react"
import DragDropArea from "./DragDropArea"
import MediaPreview from "./MediaPreview"
import { TrendingHashtags } from "@/components/post/TrendingHashtags"
import { X, ArrowLeft, ArrowRight, Check, Image as IconImage, Hash, Settings, Globe, Users, Lock, MessageCircle, Plus, UploadCloud } from "lucide-react"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { useLanguage } from "@/components/i18n/language-provider"

function getInitials(name?: string) {
  if (!name) return "U"
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length-1][0]).toUpperCase()
  return parts[0][0].toUpperCase()
}

export default function PostWizard({ onClose, onPosted, userId, avatarUrl, userName }: { onClose: () => void; onPosted?: () => void; userId?: number; avatarUrl?: string; userName?: string }) {
  const { t } = useLanguage()
  const visibilityOptions = [
    { value: "public", labelKey: "postVisibilityPublic", icon: Globe, descKey: "postVisibilityPublicDesc" },
    { value: "friends", labelKey: "postVisibilityFriends", icon: Users, descKey: "postVisibilityFriendsDesc" },
    { value: "private", labelKey: "postVisibilityPrivate", icon: Lock, descKey: "postVisibilityPrivateDesc" },
  ]
  const [step, setStep] = useState(0)
  const [content, setContent] = useState("")
  const [files, setFiles] = useState<Array<{ file: File; preview: string; progress: number }>>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [visibility, setVisibility] = useState("public")
  const [allowComments, setAllowComments] = useState(true)
  const [loading, setLoading] = useState(false)
  const MAX_CHARS = 1000

  async function compressImage(file: File, maxWidth = 1200, quality = 0.78): Promise<File> {
    if (!file.type.startsWith("image/")) return file
    try {
      const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new window.Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(file) })
      const scale = Math.min(1, maxWidth / img.width)
      const cw = Math.round(img.width * scale); const ch = Math.round(img.height * scale)
      const canvas = document.createElement("canvas"); canvas.width = cw; canvas.height = ch
      const ctx = canvas.getContext("2d"); if (!ctx) return file
      ctx.drawImage(img, 0, 0, cw, ch)
      const blob: Blob | null = await new Promise(r => canvas.toBlob(r, "image/jpeg", quality))
      if (!blob) return file
      try { URL.revokeObjectURL(img.src) } catch {}
      return new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })
    } catch { return file }
  }

  function handleFilesSelected(selected: File[]) {
    const mapped = selected.slice(0, 9).map(f => ({ file: f, preview: URL.createObjectURL(f), progress: 0 }))
    setFiles(prev => [...prev, ...mapped])
  }

  function removeFile(i: number) { setFiles(prev => { const c = [...prev]; const r = c.splice(i, 1); r.forEach(x => { try { URL.revokeObjectURL(x.preview) } catch {} }); return c }) }
  function moveFile(i: number, d: -1 | 1) { setFiles(prev => { const c = [...prev]; const t = i + d; if (t < 0 || t >= c.length) return c; const tmp = c[t]; c[t] = c[i]; c[i] = tmp; return c }) }

  function addTag() {
    const v = tagInput.trim().toLowerCase()
    if (!v) return
    setTags(prev => Array.from(new Set([...prev, v])))
    setTagInput("")
  }

  async function submit() {
    setLoading(true)
    try {
      const extracted = (content.match(/#([a-z0-9_\-]+)/gi) || []).map(s => s.replace("#", "").toLowerCase())
      const hashtags = Array.from(new Set([...tags, ...extracted]))
      const fd = new FormData()
      fd.append("content", content || "")
      fd.append("visibility", visibility)
      fd.append("allow_comments", allowComments ? "1" : "0")
      fd.append("hashtags", JSON.stringify(hashtags))
      if (userId) fd.append("userId", String(userId))
      for (const f of files) {
        let file = f.file
        if (file.type.startsWith("image/")) file = await compressImage(file)
        fd.append("files", file, file.name)
      }
      const res = await fetch("/api/posts", { method: "POST", body: fd })
      if (res.ok) {
        onPosted?.()
        onClose()
      } else {
        const data = await res.json().catch(() => null)
        const message = data?.error || (res.status === 413 ? "Fichier trop volumineux" : t("settingsGenericError"))
        alert(message)
      }
    } catch (e) { console.error(e); alert(t("settingsGenericError")) }
    finally { setLoading(false) }
  }

  const steps = [
    { title: t("postWizardStepContent"), icon: IconImage },
    { title: t("postWizardStepMedia"), icon: IconImage },
    { title: t("postWizardStepTagsSettings"), icon: Settings },
  ]
  const progress = Math.round(((step + 1) / steps.length) * 100)
  const canPublish = content.trim() || files.length > 0
  const currentVisibility = visibilityOptions.find(v => v.value === visibility) || visibilityOptions[0]
  const inlineTags = (content.match(/#([\p{L}\p{N}_-]+)/gu) || []).map(s => s.replace("#", "").toLowerCase())
  const previewTags = Array.from(new Set([...tags, ...inlineTags]))

  return (
    <div className="fixed inset-0 z-[1000] flex items-stretch justify-center p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-neutral-950/70 backdrop-blur-md dark:bg-black/80" />
      <div
        className="relative flex h-dvh max-h-dvh w-full max-w-6xl flex-col overflow-hidden border-border/70 bg-background text-foreground shadow-[0_30px_90px_rgba(0,0,0,0.38)] animate-in fade-in slide-in-from-bottom-4 duration-300 dark:border-white/10 sm:h-auto sm:max-h-[min(760px,calc(100dvh-2rem))] sm:rounded-[28px] sm:border lg:grid lg:grid-cols-[minmax(0,1fr)_380px]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex min-h-0 flex-col">
          <div className="border-b border-border/60 bg-background/95 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top,0px))] backdrop-blur-xl dark:bg-zinc-950/95 sm:px-6 sm:py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar className="h-11 w-11 shrink-0 ring-4 ring-primary/10">
                  {avatarUrl ? (
                    <AvatarImage src={avatarUrl} alt="avatar" className="object-cover" onError={(e) => { try { (e.currentTarget as HTMLImageElement).src = "" } catch {} }} />
                  ) : (
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">{getInitials(userName)}</AvatarFallback>
                  )}
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-base font-semibold sm:text-lg">{t("postNewPublication")}</h3>
                    <span className="hidden items-center gap-1 rounded-full border border-emerald-300/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300 sm:inline-flex">
                      <currentVisibility.icon className="h-3 w-3" />
                      {t(currentVisibility.labelKey as any)}
                    </span>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{t("postStep")} {step + 1}/{steps.length} - {steps[step].title}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden w-28 sm:block">
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 via-primary to-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <button onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full border border-border/60 bg-background/70 text-muted-foreground transition-all hover:bg-muted hover:text-foreground" aria-label={t("settingsInstallButtonClose")}>
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {steps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  aria-label={s.title}
                  className={`group flex h-12 min-w-0 items-center justify-center gap-2 rounded-2xl border px-2 text-xs font-medium transition-all sm:justify-start sm:px-3 ${
                    i === step
                      ? "border-primary/25 bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                      : i < step
                        ? "border-emerald-300/35 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
                        : "border-border/60 bg-muted/30 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  }`}
                >
                  <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${i === step ? "bg-white/18" : "bg-background/70"}`}>
                    {i < step ? <Check className="h-3.5 w-3.5" /> : <s.icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="min-w-0 truncate">{s.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-[radial-gradient(circle_at_top_left,rgba(6,182,212,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.10),transparent_32%)] px-4 py-5 dark:bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.14),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.10),transparent_34%)] sm:bg-background sm:px-6 sm:py-6 dark:sm:bg-background">
            {step === 0 && (
              <div className="space-y-4">
                <div className="rounded-[24px] border border-border/70 bg-card p-3 shadow-sm dark:bg-zinc-900/80">
                  <textarea
                    value={content}
                    onChange={e => setContent(e.target.value.slice(0, MAX_CHARS))}
                    placeholder={t("postWriteSomething")}
                    className="min-h-[230px] w-full resize-none rounded-[20px] border-0 bg-transparent p-3 text-[16px] leading-7 outline-none placeholder:text-muted-foreground/55 sm:min-h-[260px] sm:text-[17px]"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/50 px-3 pt-3 text-xs text-muted-foreground">
                    <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1.5 text-amber-700 dark:text-amber-300">
                      <Hash className="h-3.5 w-3.5" />
                      {t("postHashtagTip")}
                    </div>
                    <span className={`font-medium ${content.length > MAX_CHARS * 0.9 ? "text-amber-500" : ""}`}>{content.length}/{MAX_CHARS}</span>
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <button onClick={() => setStep(1)} className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-cyan-300/30 bg-cyan-500/10 text-sm font-medium text-cyan-700 transition-all hover:-translate-y-0.5 hover:bg-cyan-500/15 dark:text-cyan-300">
                    <IconImage className="h-4 w-4" />
                    {t("postWizardStepMedia")}
                  </button>
                  <button onClick={() => setStep(2)} className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-amber-300/30 bg-amber-500/10 text-sm font-medium text-amber-700 transition-all hover:-translate-y-0.5 hover:bg-amber-500/15 dark:text-amber-300">
                    <Hash className="h-4 w-4" />
                    {t("postWizardStepTagsSettings")}
                  </button>
                  <button onClick={() => setAllowComments(v => !v)} className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-emerald-300/30 bg-emerald-500/10 text-sm font-medium text-emerald-700 transition-all hover:-translate-y-0.5 hover:bg-emerald-500/15 dark:text-emerald-300">
                    <MessageCircle className="h-4 w-4" />
                    {allowComments ? t("postCommentsEnabled") : t("postCommentsDisabled")}
                  </button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <DragDropArea isDragging={isDragging} setIsDragging={setIsDragging} onDrop={handleFilesSelected} onClick={() => fileInputRef.current?.click()}>
                  <div className="flex min-h-[300px] flex-col items-center justify-center rounded-[24px] border border-dashed border-primary/30 bg-card px-4 py-8 text-center shadow-sm dark:bg-zinc-900/80 sm:min-h-[260px]">
                    <span className="mb-4 grid h-16 w-16 place-items-center rounded-3xl bg-background shadow-sm ring-1 ring-border/70">
                      <UploadCloud className="h-7 w-7 text-primary" />
                    </span>
                    <p className="text-base font-semibold">{t("postDropMedia")}</p>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t("postAddImage")} / {t("postAddVideo")}</p>
                  </div>
                </DragDropArea>
                <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*" multiple onChange={e => handleFilesSelected(e.target.files ? Array.from(e.target.files) : [])} className="hidden" />
                {files.length > 0 && <MediaPreview files={files} onRemove={removeFile} onMove={moveFile} />}
              </div>
            )}

            {step === 2 && (
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="space-y-3">
                  <label className="text-sm font-semibold">{t("postTrendingTitle")}</label>
                  <div className="rounded-[22px] border border-border/60 bg-card p-3 shadow-sm dark:bg-zinc-900/80">
                    <TrendingHashtags onSelect={t => { setContent(p => (p && !p.endsWith(" ") ? p + " " : p) + `#${t} `); setTags(prev => Array.from(new Set([...prev, t]))) }} />
                  </div>
                  <div className="rounded-[22px] border border-border/60 bg-card p-3 shadow-sm dark:bg-zinc-900/80">
                    <label className="mb-2 block text-sm font-semibold">{t("postCustomTags")}</label>
                    <div className="flex gap-2">
                      <input
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag() } }}
                        placeholder={t("postAddTag")}
                        className="min-w-0 flex-1 rounded-2xl border border-border/60 bg-muted/30 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-primary/25"
                      />
                      <button onClick={addTag} className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-foreground text-background transition-all hover:-translate-y-0.5" aria-label={t("postAdd")}>
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {tags.map((tag, i) => (
                          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">
                            #{tag}
                            <button onClick={() => setTags(tags.filter(x => x !== tag))} className="rounded-full hover:text-destructive" aria-label={`${t("settingsInstallButtonClose")} ${tag}`}>
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-semibold">{t("postVisibility")}</label>
                    <div className="grid gap-2">
                      {visibilityOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setVisibility(opt.value)}
                          className={`flex min-h-16 items-center gap-3 rounded-2xl border px-3 py-3 text-sm transition-all ${
                            visibility === opt.value
                              ? "border-primary/25 bg-primary/10 text-primary shadow-sm"
                              : "border-border/60 bg-card hover:bg-muted/50 dark:bg-zinc-900/80"
                          }`}
                        >
                          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-background ring-1 ring-border/60">
                            <opt.icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 text-left">
                            <span className="block font-semibold">{t(opt.labelKey as any)}</span>
                            <span className="block text-xs text-muted-foreground">{t(opt.descKey as any)}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setAllowComments(v => !v)}
                    className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-sm transition-all ${allowComments ? "border-emerald-300/35 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-border/60 bg-muted/30 text-muted-foreground"}`}
                  >
                    <span className="inline-flex items-center gap-2 font-medium">
                      <MessageCircle className="h-4 w-4" />
                      {t("postAllowComments")}
                    </span>
                    <span className={`h-6 w-11 rounded-full p-1 transition ${allowComments ? "bg-emerald-500" : "bg-muted-foreground/30"}`}>
                      <span className={`block h-4 w-4 rounded-full bg-white transition ${allowComments ? "translate-x-5" : ""}`} />
                    </span>
                  </button>
                  <div className="rounded-[22px] border border-border/60 bg-card p-4 shadow-sm dark:bg-zinc-900/80 lg:hidden">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{t("postWizardStepPreview")}</p>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        <currentVisibility.icon className="h-3.5 w-3.5" />
                        {t(currentVisibility.labelKey as any)}
                      </span>
                    </div>
                    <div className="whitespace-pre-wrap break-words text-sm leading-6">
                      {content || <span className="text-muted-foreground">{t("postNoTextContent")}</span>}
                    </div>
                    {previewTags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {previewTags.map((tag, i) => <span key={i} className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">#{tag}</span>)}
                      </div>
                    )}
                    {files.length > 0 && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {files.slice(0, 3).map((f, i) => (
                          <div key={i} className="relative aspect-square overflow-hidden rounded-2xl bg-muted">
                            {f.file.type.startsWith("video/") ? (
                              <video src={f.preview} muted playsInline className="h-full w-full object-cover" />
                            ) : (
                              <img src={f.preview} alt="" className="h-full w-full object-cover" />
                            )}
                            {i === 2 && files.length > 3 && <div className="absolute inset-0 flex items-center justify-center bg-black/55 font-bold text-white">+{files.length - 3}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border/60 bg-background/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] pt-3 backdrop-blur-xl dark:bg-zinc-950/95 sm:px-6 sm:pb-3">
            <button
              onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
              className="flex h-11 items-center gap-2 rounded-2xl border border-border/60 px-4 text-sm font-medium text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
            >
              {step > 0 && <ArrowLeft className="h-4 w-4" />}
              {step > 0 ? t("backLabel") : t("commentCancel")}
            </button>
            {step < steps.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)} className="flex h-11 items-center gap-2 rounded-2xl bg-foreground px-5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:shadow-lg">
                {t("nextLabel")} <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={loading || !canPublish} className="flex h-11 items-center gap-2 rounded-2xl bg-emerald-600 px-5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-lg disabled:translate-y-0 disabled:opacity-45 disabled:shadow-none">
                {loading ? <><span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> {t("sendingLabel")}</> : <><Check className="h-4 w-4" /> {t("ctaPublish")}</>}
              </button>
            )}
          </div>
        </div>

        <aside className="hidden min-h-0 border-l border-border/60 bg-muted/20 p-5 dark:bg-zinc-950/70 lg:flex lg:flex-col">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("postWizardStepPreview")}</p>
              <p className="text-xs text-muted-foreground">{t("postYourPublication")}</p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              <currentVisibility.icon className="h-3.5 w-3.5" />
              {t(currentVisibility.labelKey as any)}
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto rounded-[24px] border border-border/60 bg-card p-4 shadow-sm dark:bg-zinc-900/80">
            <div className="flex items-start gap-3">
              <Avatar className="h-10 w-10 shrink-0 ring-4 ring-primary/10">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt="avatar" className="object-cover" onError={(e) => { try { (e.currentTarget as HTMLImageElement).src = "" } catch {} }} />
                ) : (
                  <AvatarFallback className="bg-primary/10 text-primary">{getInitials(userName)}</AvatarFallback>
                )}
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{userName || t("postYourPublication")}</p>
                <p className="text-xs text-muted-foreground">{allowComments ? t("postCommentsEnabled") : t("postCommentsDisabled")}</p>
              </div>
            </div>
            <div className="mt-4 whitespace-pre-wrap break-words text-sm leading-6">
              {content || <span className="text-muted-foreground">{t("postNoTextContent")}</span>}
            </div>
            {previewTags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {previewTags.map((tag, i) => <span key={i} className="text-xs font-medium text-primary">#{tag}</span>)}
              </div>
            )}
            {files.length > 0 ? (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {files.slice(0, 4).map((f, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-2xl bg-muted">
                    {f.file.type.startsWith("video/") ? (
                      <video src={f.preview} muted playsInline className="h-full w-full object-cover" />
                    ) : (
                      <img src={f.preview} alt="" className="h-full w-full object-cover" />
                    )}
                    {i === 3 && files.length > 4 && <div className="absolute inset-0 flex items-center justify-center bg-black/55 font-bold text-white">+{files.length - 4}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 flex aspect-video items-center justify-center rounded-2xl border border-dashed border-border/70 bg-muted/30 text-xs text-muted-foreground">
                {t("postWizardStepMedia")}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}
